// Cyber Invaders: a Space-Invaders style cabinet in the arcade hall. Ships
// carry a file name, a domain or a sign-in action; the learner shoots the
// risky ones and lets the safe ones pass. The canvas (engine.ts) draws the
// playfield; every other word is DOM in the shared arcade look (../arcade).
// Rules, scoring and the decision tally are in logic.ts.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useSim } from "../../state/store";
import { audio } from "../../lib/audio";
import type { InvadersConfig, ScreenBox } from "../../scenario/types";
import {
  ArcadeBezel,
  ArcadeButton,
  ArcadeHud,
  ArcadePanel,
  CrtOverlay,
  ExitButton,
  QuestionBox,
  bigRects,
  cabinetRect,
  padScore,
  useEscapeExit,
  useViewport,
} from "../arcade/Kit";
import { arcadeSfx } from "../arcade/sfx";
import { exitArcadeDevice, makeResult, saveResult } from "../arcade/results";
import { reportCabinet, type DecisionRecord } from "../arcade/report";
import { FrogWarning, useMoveOnLabel } from "../arcade/Outcome";
import { InvadersEngine } from "./engine";
import {
  CATEGORY_LESSON,
  InvadersGame,
  MAX_SHIELDS,
  PASS_MARK,
  buildWaves,
  type ShipDef,
  type Summary,
  type WaveResult,
} from "./logic";
import "./invaders.css";
import { tx, txr } from "../../lib/tx";

type Phase =
  | "attract"
  | "credit"
  | "expanding"
  | "menu"
  | "intro"
  | "live"
  | "clear"
  | "down"
  | "end"
  | "collapsing";

const REVIEW_MAX = 6;

declare global {
  interface Window {
    __cinvDebug?: {
      state: () => unknown;
      finishWave: (mistakes?: number) => void;
    };
  }
}

export function Invaders({
  config,
  screenBox,
  onDone,
}: {
  config: InvadersConfig;
  screenBox: ScreenBox | null;
  onDone: () => void;
}) {
  const title = (config.title ?? "Cyber Invaders").toUpperCase();
  const vp = useViewport();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const glassRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<InvadersEngine | null>(null);
  const gameRef = useRef<InvadersGame | null>(null);
  const timers = useRef<number[]>([]);
  const touch = useRef<{ id: number; x0: number; y0: number; t: number; moved: boolean } | null>(null);

  const [phase, setPhase] = useState<Phase>("attract");
  const [paused, setPaused] = useState(false);
  const [soundOn, setSoundOn] = useState(() => arcadeSfx.isEnabled());
  const [reduced] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [score, setScore] = useState(0);
  const [shields, setShields] = useState(MAX_SHIELDS);
  const [combo, setCombo] = useState(0);
  const [waveIdx, setWaveIdx] = useState(0);
  const [loadSeq, setLoadSeq] = useState(0);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const [flash, setFlash] = useState(0);
  const [clearInfo, setClearInfo] = useState<WaveResult | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  /** Tally saved at the last game over (shown on the GAME OVER panel). */
  const [overInfo, setOverInfo] = useState<Summary | null>(null);
  /** A run was started and has not reached its end (results or game over). */
  const runOpen = useRef(false);
  const moveOn = useMoveOnLabel();

  const g = useRef({ phase: "attract" as Phase, paused: false, reported: false, toastId: 0 });
  g.current.phase = phase;
  g.current.paused = paused;

  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    timers.current.push(id);
  }, []);
  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    },
    [],
  );

  useEffect(() => arcadeSfx.subscribe(() => setSoundOn(arcadeSfx.isEnabled())), []);

  const big = phase !== "attract" && phase !== "credit" && phase !== "collapsing";
  const animating = phase === "expanding" || phase === "collapsing";
  const rects = bigRects(vp);
  const cab = cabinetRect(vp, screenBox);
  const stage = big ? rects.glass : cab;
  const stageStyle: CSSProperties = { left: stage.left, top: stage.top, width: stage.width, height: stage.height };

  useEffect(() => {
    if (!big) return;
    audio.holdMediaDuck("invaders");
    return () => audio.releaseMediaDuck("invaders");
  }, [big]);

  // ------------------------------------------------------------ HUD sync
  const syncHud = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;
    setScore(game.score);
    setShields(game.shields);
    setCombo(game.combo);
    setWaveIdx(Math.min(game.waveIdx, game.waves.length - 1));
  }, []);

  const showToast = useCallback(
    (text: string) => {
      const id = ++g.current.toastId;
      setToast({ id, text });
      later(() => setToast((t) => (t && t.id === id ? null : t)), 4200);
    },
    [later],
  );

  const lifeLost = useCallback(() => {
    engineRef.current?.shake();
    setFlash((f) => f + 1);
  }, []);

  // Save the run as it stands. A game over is the end of the run and is
  // saved for good; a run that is left early only counts while the cabinet
  // has no result yet (see saveCabinetResult).
  const saveRun = useCallback((game: InvadersGame, finished: boolean) => {
    const s = game.partialSummary();
    const result = makeResult("invaders", {
      rawScore: s.score,
      correct: s.correct,
      incorrect: s.incorrect,
      total: s.total,
      mistakes: s.mistakes,
      completed: false,
    });
    saveResult(result, { finished });
    if (!finished) return null;
    const decisions: DecisionRecord[] = [...game.committed, ...game.attemptDecisions].map((d) => ({
      id: d.id,
      topic: CATEGORY_LESSON[d.category].topic,
      result: d.correct ? "correct" : "incorrect",
      points: d.points,
      detail: `${d.label}: ${d.action}`,
    }));
    void reportCabinet(useSim.getState().currentBeatId ?? "", "invaders", result, decisions);
    return s;
  }, []);

  const outOfLives = useCallback(() => {
    engineRef.current?.halt();
    later(() => {
      if (g.current.phase !== "live") return;
      engineRef.current?.setMode("idle");
      arcadeSfx.gameOver();
      const game = gameRef.current;
      if (game) {
        runOpen.current = false;
        setOverInfo(saveRun(game, true));
      }
      setPhase("down");
    }, reduced ? 200 : 1100);
  }, [later, reduced, saveRun]);

  // ------------------------------------------------------------ engine events
  const events = useRef({
    onHit: (_d: ShipDef, _x: number, _y: number) => {},
    onPass: (_d: ShipDef, _x: number, _y: number) => {},
    onCleared: () => {},
  });
  events.current.onHit = (def, x, y) => {
    const game = gameRef.current;
    const e = engineRef.current;
    if (!game || !e || g.current.phase !== "live") return;
    const o = game.shoot(def);
    if (!o) return;
    syncHud();
    if (def.risky) {
      arcadeSfx.explode();
      if (o.comboBonus > 0) arcadeSfx.combo();
      e.float(x, y, o.comboBonus > 0 ? `+${o.points} COMBO` : `+${o.points}`, "good");
    } else {
      arcadeSfx.warp();
      e.float(x, y, "-50", "bad");
      showToast(`${def.label} was safe. ${def.lesson}`);
      lifeLost();
      if (o.shieldsOut) outOfLives();
    }
  };
  events.current.onPass = (def, x, y) => {
    const game = gameRef.current;
    const e = engineRef.current;
    if (!game || !e || g.current.phase !== "live") return;
    const o = game.pass(def);
    if (!o) return;
    syncHud();
    if (def.risky) {
      arcadeSfx.shieldHit();
      e.float(x, y, "GOT THROUGH", "bad");
      showToast(`${def.label} got through. ${def.lesson}`);
      lifeLost();
      if (o.shieldsOut) outOfLives();
    }
  };
  events.current.onCleared = () => {
    const game = gameRef.current;
    if (!game || g.current.phase !== "live" || game.shields <= 0) return;
    const r = game.finishWave();
    if (!r) return;
    setClearInfo(r);
    setScore(game.score);
    setCombo(0);
    arcadeSfx.correct();
    setPhase("clear");
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const e = new InvadersEngine(c, {
      onHit: (d, x, y) => events.current.onHit(d, x, y),
      onPass: (d, x, y) => events.current.onPass(d, x, y),
      onCleared: () => events.current.onCleared(),
    });
    engineRef.current = e;
    e.setReducedMotion(reduced);
    e.setMode("demo");
    e.start();
    const ro = new ResizeObserver(() => e.resize());
    ro.observe(c);
    return () => {
      ro.disconnect();
      e.stop();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    engineRef.current?.setPaused(paused);
  }, [paused]);

  // Measure the rule box so rows enter below it, then release the wave.
  useLayoutEffect(() => {
    if (phase !== "live") return;
    const e = engineRef.current;
    const game = gameRef.current;
    const glass = glassRef.current;
    if (!e || !game || !game.wave || !glass) return;
    e.resize();
    const gr = glass.getBoundingClientRect();
    const qb = glass.querySelector(".arc-qbox");
    if (qb && gr.height > 0) {
      const r = qb.getBoundingClientRect();
      e.setPlayTop((r.bottom - gr.top) / gr.height + 0.025);
    } else e.setPlayTop(0.2);
    e.loadWave(game.wave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSeq]);

  // ------------------------------------------------------------ flow
  const insertCoin = useCallback(() => {
    arcadeSfx.unlock();
    arcadeSfx.coin();
    setPhase("credit");
  }, []);

  const pressStart = useCallback(() => {
    arcadeSfx.unlock();
    arcadeSfx.start();
    setPhase("expanding");
    later(() => setPhase("menu"), reduced ? 60 : 720);
  }, [later, reduced]);

  const toIntro = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;
    game.startWave();
    syncHud();
    setToast(null);
    engineRef.current?.setMode("idle");
    setPhase("intro");
  }, [syncHud]);

  const startGame = useCallback(() => {
    arcadeSfx.unlock();
    arcadeSfx.select();
    gameRef.current = new InvadersGame(buildWaves());
    g.current.reported = false;
    runOpen.current = true;
    setSummary(null);
    setClearInfo(null);
    setPaused(false);
    toIntro();
  }, [toIntro]);

  const beginWave = useCallback(() => {
    arcadeSfx.unlock();
    arcadeSfx.start();
    setToast(null);
    setPhase("live");
    setLoadSeq((n) => n + 1);
  }, []);

  const retryWave = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;
    arcadeSfx.unlock();
    arcadeSfx.start();
    game.retryWave();
    runOpen.current = true;
    syncHud();
    setToast(null);
    setPhase("live");
    setLoadSeq((n) => n + 1);
  }, [syncHud]);

  const finishGame = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;
    const s = game.summary();
    runOpen.current = false;
    setSummary(s);
    setScore(s.score);
    engineRef.current?.setMode("demo");
    setPhase("end");
    arcadeSfx.fanfare();
    if (g.current.reported) return;
    g.current.reported = true;
    const result = makeResult("invaders", {
      rawScore: s.score,
      correct: s.correct,
      incorrect: s.incorrect,
      total: s.total,
      mistakes: s.mistakes,
    });
    saveResult(result);
    const decisions: DecisionRecord[] = game.committed.map((d) => ({
      id: d.id,
      topic: CATEGORY_LESSON[d.category].topic,
      result: d.correct ? "correct" : "incorrect",
      points: d.points,
      detail: `${d.label}: ${d.action}`,
    }));
    void reportCabinet(useSim.getState().currentBeatId ?? "", "invaders", result, decisions);
  }, []);

  const nextFromClear = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;
    arcadeSfx.select();
    if (game.done) finishGame();
    else toIntro();
  }, [finishGame, toIntro]);

  const leave = useCallback(() => {
    arcadeSfx.unlock();
    const ph = g.current.phase;
    if (ph === "attract" || ph === "credit") {
      exitArcadeDevice(onDone, "invaders");
      return;
    }
    if (ph === "collapsing") return;
    if (runOpen.current && gameRef.current) {
      runOpen.current = false;
      saveRun(gameRef.current, false);
    }
    setPaused(false);
    setToast(null);
    engineRef.current?.setMode("demo");
    setPhase("collapsing");
    later(() => exitArcadeDevice(onDone, "invaders"), reduced ? 60 : 720);
  }, [later, onDone, reduced, saveRun]);

  const pausable = phase === "live" || phase === "intro" || phase === "clear" || phase === "down";
  const onEscape = useCallback(() => {
    if (pausable) {
      arcadeSfx.select();
      setPaused((p) => !p);
    } else leave();
  }, [leave, pausable]);
  useEscapeExit(onEscape);

  const toggleSound = () => {
    const on = !soundOn;
    arcadeSfx.setEnabled(on);
    setSoundOn(on);
    if (on) {
      arcadeSfx.unlock();
      arcadeSfx.coin();
    }
  };

  const fire = () => {
    if (engineRef.current?.fire()) arcadeSfx.laser();
  };

  // ------------------------------------------------------------ keyboard
  useEffect(() => {
    const keyDir = (k: string) =>
      k === "ArrowLeft" || k === "a" || k === "A" ? "left" : k === "ArrowRight" || k === "d" || k === "D" ? "right" : null;
    const down = (e: KeyboardEvent) => {
      const st = g.current;
      const k = e.key;
      let used = true;
      const dir = keyDir(k);
      if (st.phase === "live" && !st.paused && dir) {
        engineRef.current?.setKey(dir, true);
      } else if (st.phase === "live" && !st.paused && (k === " " || k === "Spacebar")) {
        fire();
      } else if (k === "Enter" || ((k === " " || k === "Spacebar") && (st.phase === "attract" || st.phase === "credit"))) {
        if (e.repeat) {
          /* ignore held Enter */
        } else if (st.paused) setPaused(false);
        else if (st.phase === "attract") insertCoin();
        else if (st.phase === "credit") pressStart();
        else if (st.phase === "menu") startGame();
        else if (st.phase === "intro") beginWave();
        else if (st.phase === "clear") nextFromClear();
        else if (st.phase === "down") retryWave();
        else used = false;
      } else if (st.phase === "live" && (dir || k === " ")) {
        // paused: swallow game keys so the walk controls stay still
      } else {
        used = false;
      }
      if (used) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const up = (e: KeyboardEvent) => {
      const dir = keyDir(e.key);
      if (dir) engineRef.current?.setKey(dir, false);
      if (g.current.phase === "live" && (dir || e.key === " ")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const blur = () => {
      engineRef.current?.setKey("left", false);
      engineRef.current?.setKey("right", false);
    };
    window.addEventListener("keydown", down, true);
    window.addEventListener("keyup", up, true);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down, true);
      window.removeEventListener("keyup", up, true);
      window.removeEventListener("blur", blur);
    };
  });

  // ------------------------------------------------------------ pointer
  const onPointerDown = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (g.current.phase !== "live" || g.current.paused) return;
    if ((ev.target as HTMLElement).closest("button")) return;
    const e = engineRef.current;
    if (!e) return;
    ev.preventDefault();
    e.setTargetX(e.toLogicalX(ev.clientX));
    if (ev.pointerType === "mouse") {
      if (ev.button === 0) fire();
      return;
    }
    touch.current = { id: ev.pointerId, x0: ev.clientX, y0: ev.clientY, t: performance.now(), moved: false };
    try {
      ev.currentTarget.setPointerCapture(ev.pointerId);
    } catch {
      /* capture is optional */
    }
  };
  const onPointerMove = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (g.current.phase !== "live" || g.current.paused) return;
    const e = engineRef.current;
    if (!e) return;
    if (ev.pointerType === "mouse") {
      e.setTargetX(e.toLogicalX(ev.clientX));
      return;
    }
    const t = touch.current;
    if (!t || t.id !== ev.pointerId) return;
    e.setTargetX(e.toLogicalX(ev.clientX));
    if (Math.hypot(ev.clientX - t.x0, ev.clientY - t.y0) > 12) t.moved = true;
  };
  const onPointerUp = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const t = touch.current;
    if (!t || t.id !== ev.pointerId) return;
    touch.current = null;
    if (g.current.phase === "live" && !g.current.paused && !t.moved && performance.now() - t.t < 350) fire();
  };

  // ------------------------------------------------------------ debug hook
  useEffect(() => {
    window.__cinvDebug = {
      state: () => ({
        phase: g.current.phase,
        paused: g.current.paused,
        wave: gameRef.current?.wave?.n ?? null,
        score: gameRef.current?.score ?? 0,
        shields: gameRef.current?.shields ?? 0,
        ships: engineRef.current?.debugShips() ?? [],
      }),
      finishWave: (mistakes = 0) => engineRef.current?.debugFinish(mistakes),
    };
    return () => {
      delete window.__cinvDebug;
    };
  }, []);

  // ------------------------------------------------------------ render
  const game = gameRef.current;
  const wave = game?.waves[waveIdx];
  const nWaves = game?.waves.length ?? 3;
  const hud = phase === "intro" || phase === "live" || phase === "clear" || phase === "down";
  const ctlTop = Math.min(cab.top + cab.height + 14, vp.h - 64);
  const review = summary ? summary.missed.slice(0, REVIEW_MAX) : [];
  const moreMissed = summary ? summary.missed.length - review.length : 0;

  return (
    <div className={`arc-root cinv-root cinv-${phase}${reduced ? " cinv-reduced" : ""}`}>
      <div className={`arc-scrim${big ? " is-on" : ""}`} />
      <ArcadeBezel frame={rects.frame} side={rects.side} visible={big && !animating} />

      <div
        ref={glassRef}
        className={`arc-glass cinv-glass${big ? " is-big" : " is-cab"}${animating ? " cinv-anim" : ""}${
          phase === "live" ? " is-live" : ""
        }`}
        style={stageStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <canvas ref={canvasRef} className="cinv-canvas" aria-hidden="true" />
        {flash > 0 && !reduced && <div key={flash} className="cinv-flash" aria-hidden="true" />}

        {(phase === "attract" || phase === "credit" || phase === "collapsing") && (
          <div className="cinv-attract">
            <div className="cinv-logo">{title}</div>
            {phase === "attract" && <div className="cinv-blink arc-blink">{tx("INSERT COIN")}</div>}
            {phase === "credit" && (
              <>
                <div className="cinv-credit">{tx("CREDIT 1")}</div>
                <div className="cinv-blink arc-blink">{tx("PRESS START")}</div>
              </>
            )}
            {phase === "collapsing" && <div className="cinv-credit">{tx("THANKS FOR PLAYING")}</div>}
          </div>
        )}

        {hud && (
          <ArcadeHud
            score={score}
            wave={{ label: "WAVE", value: `${waveIdx + 1}/${nWaves}` }}
            lives={{ n: shields, max: MAX_SHIELDS }}
            extra={
              <>
                {phase === "live" && combo >= 2 && <span className="cinv-combo">{txr("COMBO x{combo}", { combo })}</span>}
                <button
                  type="button"
                  className="arc-mini"
                  onClick={toggleSound}
                  aria-label={soundOn ? tx("Turn sound off") : tx("Turn sound on")}
                >
                  {soundOn ? tx("SND ON") : tx("SND OFF")}
                </button>
                {phase === "live" && (
                  <button type="button" className="arc-mini" onClick={() => setPaused(true)}>
                    {tx("PAUSE")}
                  </button>
                )}
              </>
            }
          />
        )}

        {phase === "live" && wave && <QuestionBox title={wave.title} subtitle={wave.subtitle} />}

        {phase === "live" && toast && (
          <div key={toast.id} className="cinv-toast" role="status">
            {toast.text}
          </div>
        )}

        {phase === "menu" && (
          <ArcadePanel className="cinv-panel cinv-menu">
            <div className="arc-title">{title}</div>
            <p className="arc-text">
              {tx("Each ship carries a file name, a web address or a sign-in action. Shoot the risky ones and let the safe ones pass.")}
            </p>
            <ul className="cinv-how">
              <li>
                {txr("<0>Move:</0> arrow keys or A and D, the mouse, or drag with a finger.", {}, [<b />])}
              </li>
              <li>
                {txr("<0>Fire:</0> Space, click, or tap.", {}, [<b />])}
              </li>
              <li>
                {txr("<0>Risky ship hit:</0> +100, more for a streak. <1>Safe ship hit:</1> -50 and a life.", {}, [<b />, <b />])}
              </li>
              <li>{tx("A risky ship that reaches the line costs a life. Lose all three and the wave starts again.")}</li>
            </ul>
            <div className="arc-actions">
              <ArcadeButton onClick={toggleSound}>{txr("SOUND: {v}", { v: soundOn ? tx("ON") : tx("OFF") })}</ArcadeButton>
              <ArcadeButton onClick={leave}>{tx("LEAVE")}</ArcadeButton>
              <ArcadeButton primary onClick={startGame}>
                {tx("START")}
              </ArcadeButton>
            </div>
          </ArcadePanel>
        )}

        {phase === "intro" && wave && (
          <ArcadePanel className="cinv-panel cinv-intro">
            <div className="arc-sub">
              {txr("WAVE {n} OF {nWaves}", { n: wave.n, nWaves })}
            </div>
            <div className="arc-title">{wave.title}</div>
            {wave.n === 2 && <div className="cinv-trusted">{wave.subtitle}</div>}
            <p className="arc-text">{wave.rule}</p>
            <p className="arc-text cinv-small">
              {txr("{length} ships. Lives refilled to {MAX_SHIELDS}", { length: wave.ships.length, MAX_SHIELDS })}.
            </p>
            <div className="arc-actions">
              <ArcadeButton primary onClick={beginWave}>
                {tx("START WAVE")}
              </ArcadeButton>
            </div>
          </ArcadePanel>
        )}

        {phase === "clear" && clearInfo && (
          <ArcadePanel className="cinv-panel">
            <div className="arc-sub">{txr("WAVE {wave} CLEAR", { wave: clearInfo.wave })}</div>
            <div className="arc-title">
              {clearInfo.correct} / {clearInfo.total}
            </div>
            <p className="arc-text">{tx("correct decisions in this wave")}</p>
            <p className="arc-text cinv-small">
              {clearInfo.clean
                ? tx("Clean wave bonus: +{bonus}", { bonus: clearInfo.bonus })
                : tx("No clean-wave bonus this time. Every mistake is listed at the end.")}
            </p>
            <div className="arc-actions">
              <ArcadeButton primary onClick={nextFromClear}>
                {game?.done ? tx("SEE RESULTS") : tx("NEXT WAVE")}
              </ArcadeButton>
            </div>
          </ArcadePanel>
        )}

        {phase === "down" && wave && (
          <ArcadePanel className="cinv-panel">
            <div className="arc-title">{tx("GAME OVER")}</div>
            <p className="arc-text">
              {txr("Your result is saved: {v}% correct, below the {v2}% pass mark. RETRY WAVE starts wave {n} again with {MAX_SHIELDS} lives; finish every wave and the new result replaces this one.", { v: overInfo ? Math.round(overInfo.accuracy * 100) : 0, v2: Math.round(PASS_MARK * 100), n: wave.n, MAX_SHIELDS })}
            </p>
            <FrogWarning />
            <div className="arc-actions">
              <ArcadeButton onClick={leave}>{moveOn}</ArcadeButton>
              <ArcadeButton primary onClick={retryWave}>
                {tx("RETRY WAVE")}
              </ArcadeButton>
            </div>
          </ArcadePanel>
        )}

        {phase === "end" && summary && (
          <ArcadePanel wide className="cinv-panel cinv-end">
            <div className="arc-title">{tx("MISSION COMPLETE")}</div>
            <div className="arc-sub">{summary.rank}</div>
            <div className="cinv-stats">
              <div>
                <span className="arc-label">{tx("SCORE")}</span>
                <b className="cinv-score">{padScore(summary.score, 6)}</b>
              </div>
              <div>
                <span className="arc-label">{tx("ACCURACY")}</span>
                <b>{Math.round(summary.accuracy * 100)}%</b>
              </div>
              <div>
                <span className="arc-label">{tx("CORRECT")}</span>
                <b>
                  {summary.correct}/{summary.total}
                </b>
              </div>
              <div>
                <span className="arc-label">{tx("RETRIES")}</span>
                <b>{summary.retries}</b>
              </div>
            </div>
            <p className="arc-text cinv-small">
              {summary.passed
                ? tx("Pass mark of {v}% reached.", { v: Math.round(PASS_MARK * 100) })
                : tx("Below the {v}% pass mark. Look at the items below and play again.", { v: Math.round(PASS_MARK * 100) })}
            </p>
            <div className="cinv-review">
              <span className="arc-label">{tx("WHAT TO REVIEW")}</span>
              {review.length ? (
                <ul>
                  {review.map((m) => (
                    <li key={m.label}>
                      <b>{m.label}</b>
                      <span>{m.lesson}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="arc-text cinv-small">{tx("Nothing missed. Every ship was handled correctly.")}</p>
              )}
              {moreMissed > 0 && <p className="arc-text cinv-small">{txr("and {moreMissed} more", { moreMissed })}</p>}
            </div>
            {!summary.passed && <FrogWarning />}
            <div className="arc-actions">
              <ArcadeButton primary={!summary.passed} onClick={startGame}>
                {summary.passed ? tx("PLAY AGAIN") : tx("TRY AGAIN")}
              </ArcadeButton>
              <ArcadeButton primary={summary.passed} onClick={leave}>
                {moveOn}
              </ArcadeButton>
            </div>
          </ArcadePanel>
        )}

        {paused && (
          <ArcadePanel className="cinv-panel cinv-pause">
            <div className="arc-title">{tx("PAUSED")}</div>
            <div className="arc-actions">
              <ArcadeButton onClick={leave}>{tx("EXIT")}</ArcadeButton>
              <ArcadeButton primary onClick={() => setPaused(false)}>
                {tx("RESUME")}
              </ArcadeButton>
            </div>
          </ArcadePanel>
        )}

        <CrtOverlay />
      </div>

      {(phase === "attract" || phase === "credit") && (
        <div className="cinv-cabctl" style={{ top: ctlTop }}>
          {phase === "attract" ? (
            <ArcadeButton primary onClick={insertCoin}>
              {tx("INSERT COIN")}
            </ArcadeButton>
          ) : (
            <ArcadeButton primary onClick={pressStart}>
              {tx("PRESS START")}
            </ArcadeButton>
          )}
          <ArcadeButton onClick={leave}>{tx("LEAVE CABINET")}</ArcadeButton>
        </div>
      )}

      <ExitButton onExit={leave} />
    </div>
  );
}
